import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Send, ArrowLeft, Save, Printer, AlertTriangle } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const CUR = "\u20b9";
const fmt = (n) => (n === null || n === undefined ? "—" : `${CUR}${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);

export default function AdminQuoteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [charges, setCharges] = useState([]);
  const [advance, setAdvance] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [gstSettings, setGstSettings] = useState({ enabled: true, rate: 5 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/quotes/${id}`);
      setQ(data);
      setCharges(data.additional_charges || []);
      setAdvance(data.advance_requested != null ? String(data.advance_requested) : "");
      setNotes(data.admin_notes || "");
      const s = await api.get("/settings");
      setGstSettings({ enabled: s.data.gst_enabled !== false, rate: s.data.gst_rate != null ? Number(s.data.gst_rate) : 5 });
    } catch (e) { toast.error(errMsg(e, "Load failed")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addCharge = () => setCharges((c) => [...c, { label: "", amount: 0, note: "" }]);
  const removeCharge = (i) => setCharges((c) => c.filter((_, idx) => idx !== i));
  const updCharge = (i, patch) => setCharges((c) => c.map((row, idx) => idx === i ? { ...row, ...patch } : row));

  const save = async () => {
    const unlabeled = charges.some((c) => !(c.label || "").trim() && (Number(c.amount) || 0) > 0);
    if (unlabeled) {
      toast.error("Every charge with an amount needs a label — add a label or remove the row");
      return;
    }
    setSaving(true);
    try {
      const cleaned = charges.map((c) => ({
        label: (c.label || "").trim(),
        amount: Number(c.amount) || 0,
        note: c.note || null,
      })).filter((c) => c.label);
      const payload = {
        additional_charges: cleaned,
        advance_requested: advance === "" ? null : Number(advance),
        admin_notes: notes,
      };
      const { data } = await api.patch(`/admin/quotes/${id}`, payload);
      setQ(data);
      setCharges(data.additional_charges || []);
      setAdvance(data.advance_requested != null ? String(data.advance_requested) : "");
      toast.success("Quote saved");
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const send = async () => {
    if (q.final_amount == null) {
      toast.error("Save additional charges first to set the final amount");
      return;
    }
    if (!window.confirm(`Send this quote to ${q.customer_name || "the customer"} for approval?`)) return;
    setSending(true);
    try {
      await api.post(`/admin/quotes/${id}/send`);
      toast.success("Quote sent to customer");
      load();
    } catch (e) { toast.error(errMsg(e, "Send failed")); }
    finally { setSending(false); }
  };

  if (loading || !q) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const gstCfg = q.gst_snapshot;
  const preview = (() => {
    const sub = q.pricing_snapshot?.subtotal ?? q.pricing_snapshot?.subtotal_partial ?? 0;
    const chg = charges.reduce((s, c) => s + (Number(c.amount) || 0), 0);
    const taxable = Number(sub) + chg;
    const enabled = gstCfg ? gstCfg.enabled : gstSettings.enabled;
    const rate = gstCfg && gstCfg.enabled ? gstCfg.rate : gstSettings.rate;
    const gst = enabled ? Math.round(taxable * rate) / 100 : 0;
    return { taxable, enabled, rate, gst, final: Math.round((taxable + gst) * 100) / 100 };
  })();
  const finalAmount = preview.final;

  const editable = !["approved", "declined", "expired"].includes(q.status);

  // Unsaved-changes detection: the pricing panel is a live PREVIEW of the form;
  // the customer link/PDF only ever shows SAVED data.
  const _norm = (list) =>
    JSON.stringify((list || [])
      .filter((c) => (c.label || "").trim())
      .map((c) => [(c.label || "").trim(), Number(c.amount) || 0, c.note || ""]));
  const dirty =
    _norm(charges) !== _norm(q.additional_charges) ||
    advance !== (q.advance_requested != null ? String(q.advance_requested) : "") ||
    (notes || "") !== (q.admin_notes || "");

  return (
    <div className="mx-auto max-w-5xl">
      <Link to="/admin/quotes" data-testid="admin-quote-back" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to quotes
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="kbd-eyebrow">Admin</span>
          <h1 className="mt-2 font-serif-display text-3xl font-semibold text-secondary">Quote editor</h1>
          <div className="mt-2 text-sm text-muted-foreground">
            {q.customer_name} · {q.mobile} · <span className="uppercase tracking-[0.14em]" data-testid="admin-quote-status">{q.status}</span>
          </div>
          {!q.customer_id && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                data-testid="admin-quote-guest-badge"
                className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-800"
              >
                Guest enquiry — WhatsApp follow-up only
              </span>
              <button
                data-testid="admin-copy-customer-link"
                onClick={() => {
                  if (dirty || q.final_amount == null) {
                    toast.error("Save changes first — the customer link shows only saved pricing");
                    return;
                  }
                  navigator.clipboard.writeText(`${window.location.origin}/quote/${q.id}/view`);
                  toast.success("Customer link copied — share it on WhatsApp");
                }}
                className="inline-flex items-center rounded-full border border-border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-secondary hover:bg-muted"
              >
                Copy customer link
              </button>
            </div>
          )}
          {q.customer_confirmed_at && (
            <span
              data-testid="admin-quote-confirmed-badge"
              className="mt-2 inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-800"
            >
              Customer confirmed · {new Date(q.customer_confirmed_at).toLocaleString("en-IN")}
            </span>
          )}
        </div>
        {editable && (
          <div className="flex gap-2">
            <a
              data-testid="admin-quote-print-link"
              href={`/admin/quotes/${id}/print`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (dirty) {
                  e.preventDefault();
                  toast.error("Save changes first — the print view shows only saved pricing");
                }
              }}
              className="btn-ghost !py-2 !text-xs"
            >
              <Printer className="h-3.5 w-3.5" /> Print
            </a>
            <button data-testid="admin-quote-save" disabled={saving} onClick={save} className="btn-ghost !py-2 !text-xs">
              <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save changes"}
            </button>
            {q.status === "prepared" ? (
              <span
                data-testid="admin-quote-sent-badge"
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-4 py-2 text-xs font-semibold text-emerald-800"
              >
                <Send className="h-3.5 w-3.5" /> Sent to customer — awaiting approval
              </span>
            ) : (
              <button
                data-testid="admin-quote-send"
                disabled={sending || q.final_amount == null || !q.customer_id}
                onClick={send}
                className="btn-primary !py-2 !text-xs"
              >
                <Send className="h-3.5 w-3.5" /> {sending ? "Sending…" : "Send to customer"}
              </button>
            )}
          </div>
        )}
      </div>

      {editable && dirty && (
        <div
          data-testid="admin-quote-unsaved-banner"
          className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            <strong>Unsaved changes</strong> — the totals shown are a preview. Click{" "}
            <strong>Save changes</strong> to update what the customer sees on their quote
            link / PDF.
          </span>
        </div>
      )}

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_360px]">
        <div className="space-y-6">
          <section className="surface-card">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Event snapshot</div>
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <Info label="Event" v={q.event_type} />
              <Info label="Date" v={q.event_date} />
              <Info label="Time" v={q.event_time} />
              <Info label="Guests" v={q.guest_count} />
              <Info label="Venue" v={q.venue} full />
              <Info label="Package" v={q.package_snapshot?.name || (q.selected_menu || []).find((m) => m.kind === "package")?.name} full />
              <Info label="Special requirements" v={q.special_requirements || "Not specified"} full />
            </dl>
          </section>

          <section className="surface-card" data-testid="admin-charges-section">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Additional charges</div>
              {editable && (
                <button data-testid="admin-add-charge" onClick={addCharge} className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-semibold text-secondary hover:bg-muted">
                  <Plus className="h-3.5 w-3.5" /> Add row
                </button>
              )}
            </div>
            {charges.length === 0 && <div className="mt-3 rounded-xl border border-dashed border-border/70 bg-background p-4 text-xs text-muted-foreground">No additional charges yet.</div>}
            <ul className="mt-3 space-y-2">
              {charges.map((c, i) => (
                <li key={i} data-testid={`admin-charge-row-${i}`} className="grid gap-2 rounded-xl border border-border/70 bg-background p-3 sm:grid-cols-[1.5fr_120px_1.5fr_auto]">
                  <input data-testid={`admin-charge-label-${i}`} disabled={!editable} placeholder="Label (e.g. Live Counter — Chaat)" value={c.label} onChange={(e) => updCharge(i, { label: e.target.value })} className="rounded-lg border border-border/70 bg-background px-3 py-1.5 text-sm" />
                  <input data-testid={`admin-charge-amount-${i}`} disabled={!editable} type="number" min="0" placeholder="Amount ₹" value={c.amount} onChange={(e) => updCharge(i, { amount: e.target.value })} className="rounded-lg border border-border/70 bg-background px-3 py-1.5 text-sm" />
                  <input data-testid={`admin-charge-note-${i}`} disabled={!editable} placeholder="Note (optional)" value={c.note || ""} onChange={(e) => updCharge(i, { note: e.target.value })} className="rounded-lg border border-border/70 bg-background px-3 py-1.5 text-sm" />
                  {editable && (
                    <button data-testid={`admin-charge-remove-${i}`} onClick={() => removeCharge(i)} className="grid h-8 w-8 place-items-center rounded-full text-destructive hover:bg-destructive/10" aria-label="Remove">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {editable && (
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Advance requested (₹)</span>
                  <input data-testid="admin-quote-advance" type="number" min="0" value={advance} onChange={(e) => setAdvance(e.target.value)} placeholder="e.g. 21000" className="w-full rounded-xl border border-border/70 bg-background px-4 py-2" />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Admin notes (internal)</span>
                  <textarea data-testid="admin-quote-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full rounded-xl border border-border/70 bg-background px-4 py-2 text-sm" />
                </label>
              </div>
            )}
          </section>
        </div>

        <aside className="surface-card h-fit lg:sticky lg:top-24">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">Live price preview</div>
          <Row label="Subtotal (from pricing engine)" v={fmt(q.pricing_snapshot?.subtotal ?? q.pricing_snapshot?.subtotal_partial)} testid="admin-preview-subtotal" />
          <Row label="Additional charges" v={fmt(charges.reduce((s, c) => s + (Number(c.amount) || 0), 0))} testid="admin-preview-charges" />
          {preview.enabled && (
            <>
              <Row label="Taxable subtotal" v={fmt(preview.taxable)} testid="admin-preview-taxable" />
              <Row label={`GST (${preview.rate}%)`} v={fmt(preview.gst)} testid="admin-preview-gst" />
            </>
          )}
          <div className="mt-3 flex items-baseline justify-between border-t border-border/70 pt-3">
            <div className="text-sm font-semibold uppercase tracking-[0.16em] text-secondary">Final ₹</div>
            <div className="text-2xl font-bold text-secondary" data-testid="admin-preview-final">{fmt(finalAmount)}</div>
          </div>
          <Row label="Advance requested" v={advance ? fmt(Number(advance)) : "—"} testid="admin-preview-advance" />
          {q.pricing_snapshot?.needs_config?.length > 0 && (
            <ul className="mt-3 space-y-1 rounded-xl border border-primary/30 bg-primary/5 p-2 text-[11px] text-secondary">
              {q.pricing_snapshot.needs_config.map((n, i) => <li key={i}>· {n}</li>)}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}

function Info({ label, v, full }) {
  return <div className={full ? "sm:col-span-2" : ""}><div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{label}</div><div className="mt-0.5 font-medium text-secondary">{v || "—"}</div></div>;
}
function Row({ label, v, testid }) {
  return <div className="mt-2 flex items-baseline justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className="font-semibold text-secondary" data-testid={testid}>{v}</span></div>;
}
